import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import { ProductV2StoryCard } from "@/shared/data/product.v2.data";
import SectionHeader from "./shared/SectionHeader";
import Text from "./shared/Text";

/**
 * StoriesMarqueeV2 — "STORIES / Creators Who Sell More, Without Doing More"
 *
 * Centered header + optional CTA pair + horizontally-scrolling marquee row
 * of testimonial cards. Each card is title + long quote + avatar + name +
 * role. The marquee bleeds past the container edges on both sides so the
 * row reads as part of a continuous rail — matching the product-page
 * reference.
 *
 * Marquee motion uses `animate-marquee-x` (defined in globals.css). Pauses
 * on hover so users can read a specific card.
 */

export type StoriesMarqueeV2Props = {
  eyebrow?: string;
  titleMuted: string;
  titleBright: string;
  subtitle?: string;
  ctas?: CtaType[];
  stories: ProductV2StoryCard[];
};

const StoryCard = ({ story }: { story: ProductV2StoryCard }) => (
  <article className="flex h-[380px] w-[320px] shrink-0 flex-col justify-between rounded-3xl bg-slateLight p-6 sm:h-[420px] sm:w-[360px] sm:p-7 md:w-[380px]">
    <div>
      <Text
        as="h3"
        variant="title-md"
        className="text-[18px] !font-medium text-neutralBlack"
      >
        {story.title}
      </Text>
      <Text
        as="p"
        variant="body-md"
        className="mt-4 text-[14px] leading-relaxed text-darkSlate04"
      >
        {story.quote}
      </Text>
    </div>
    <div className="mt-6 flex items-center gap-3">
      {story.avatar ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={story.avatar}
          alt={story.name}
          className="h-10 w-10 rounded-full object-cover"
        />
      ) : (
        // Empty avatar ring — placeholder before real headshots land.
        <div className="h-10 w-10 rounded-full bg-slate04" aria-hidden="true" />
      )}
      <div>
        <Text
          as="div"
          variant="body-md"
          className="text-[14px] font-semibold text-neutralBlack"
        >
          {story.name}
        </Text>
        <Text
          as="div"
          variant="body-sm"
          className="text-[13px] text-darkSlate04"
        >
          {story.role}
        </Text>
      </div>
    </div>
  </article>
);

const StoriesMarqueeV2 = ({
  eyebrow,
  titleMuted,
  titleBright,
  subtitle,
  ctas,
  stories,
}: StoriesMarqueeV2Props) => {
  // Duplicate the list once so the horizontal scroll loops seamlessly.
  const loop = [...stories, ...stories];
  return (
    <section className="relative w-full overflow-hidden bg-white">
      <div className="container mx-auto px-4 pt-10 sm:px-6 md:px-8 md:pt-20 lg:px-12 lg:pt-24">
        <div className="mx-auto max-w-6xl">
        <SectionHeader
          eyebrow={eyebrow}
          titleMuted={titleMuted}
          titleBright={titleBright}
          subtitle={subtitle}
          variant="light-teal"
          align="center"
        />
        {ctas && ctas.length ? (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 md:mt-10">
            <CtaButtonGroup ctas={ctas} gap="flex gap-3" fullWidthOnMobile={false} />
          </div>
        ) : null}
      </div>
      </div>

      {/* Marquee rail — horizontal scroll; pause on hover so the cursor
          can read a card without it sliding away. */}
      <div className="group relative mt-12 w-full md:mt-16">
        <div className="flex w-max gap-5 pb-6 pl-4 pr-4 animate-[feature-marquee-left_60s_linear_infinite] group-hover:[animation-play-state:paused] sm:gap-6 sm:pl-6 sm:pr-6 md:gap-8">
          {loop.map((story, idx) => (
            <StoryCard key={`${story.name}-${idx}`} story={story} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default StoriesMarqueeV2;
